import { useEffect, useRef, useState } from "react";
import { MainContainer, MainContent } from "../common/components/Layout";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { callApi } from "../common/api/Api";
import { TenantPole, userLogin } from "./AdminLoginApi";
import { getAuthItems } from "../auth/AuthItems";
import { MenuItem } from "@mui/material";

/**
 * RG担当者用のログイン画面のコンポーネントです。
 *
 * @returns RG担当者用のログイン画面のコンポーネント
 */
export const AdminLoginPage = (): JSX.Element => {
  const { t } = useTranslation();
  const inputTenantPoleRef = useRef<HTMLInputElement>();
  const inputUserIdRef = useRef<HTMLInputElement>();
  const inputPasswordRef = useRef<HTMLInputElement>();
  const [errorUserId, setErrorUserId] = useState<boolean>(false);
  const [errorUserIdMessage, setErrorUserIdMessage] = useState<string>("");
  const [errorPassword, setErrorPassword] = useState<boolean>(false);
  const [errorPasswordMessage, setErrorPasswordMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const loginTenantList = [
    { label: t("adminLogin.reTenantPole"), value: TenantPole.RE },
    { label: t("adminLogin.rjTenantPole"), value: TenantPole.RJ },
    { label: t("adminLogin.rusTenantPole"), value: TenantPole.RUS },
    { label: t("adminLogin.rlaTenantPole"), value: TenantPole.RLA },
    { label: t("adminLogin.rapTenantPole"), value: TenantPole.RAP },
    { label: t("adminLogin.rkrTenantPole"), value: TenantPole.RKR },
  ];

  useEffect(() => {
    const authItems = getAuthItems();
    authItems.removeAll();
  }, []);

  const loginClicked = () => {
    const tenantPole = inputTenantPoleRef.current?.value;
    const userId = inputUserIdRef.current?.value;
    const password = inputPasswordRef.current?.value;

    setErrorMessage("");
    if (!userId) {
      setErrorUserId(true);
      setErrorUserIdMessage(t("error.notInputUserID"));
      return;
    } else {
      setErrorUserId(false);
      setErrorUserIdMessage(t(""));
    }
    if (!password) {
      setErrorPassword(true);
      setErrorPasswordMessage(t("error.notInputPassword"));
      return;
    } else {
      setErrorPassword(false);
      setErrorPasswordMessage(t(""));
    }
    if (!tenantPole) {
      // デフォルトで選択済みなので、ここに入ることは無い
      return;
    }

    (async () => {
      await callApi(async () => {
        const result = await userLogin(tenantPole, userId, password);
        if (result.error) {
          setErrorMessage(t("error.failureLogin"));
          return;
        }
        if (!result.data || !result.data.result || !result.data.token) {
          setErrorMessage(t("error.failureLogin"));
          return;
        }

        const authItems = getAuthItems();
        authItems.accessToken = result.data.token;
        authItems.loginTime = Date.now().toString();
        authItems.loginPole = tenantPole;

        window.location.href = "/admin/menu";
      });
    })();
  };

  return (
    <MainContainer>
      <MainContent>
        <div className="bg-[#818181] h-full w-full flex justify-center items-center">
          <div className="bg-white p-6">
            <div className="flex flex-col justify-center space-y-5">
              <div className="text-2xl">{t("common.applicationName")}</div>
              <div className="text-2xl">{t("adminLogin.title")}</div>
              <div className="flex flex-col">
                <label htmlFor="inputTenant" className="text-base">
                  {t("adminLogin.countryOfCustomer")}
                </label>
                <TextField
                  id="inputTenant"
                  variant="outlined"
                  sx={{ margin: 0 }}
                  select
                  inputProps={{ maxLength: 100 }}
                  inputRef={inputTenantPoleRef}
                  defaultValue={TenantPole.RJ}
                >
                  {loginTenantList.map((item, index) => (
                    <MenuItem key={index} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
              <div className="flex flex-col">
                <label htmlFor="inputUserId" className="text-base">
                  {t("common.userId")}
                </label>
                <TextField
                  id="inputUserId"
                  variant="outlined"
                  sx={{ margin: 0 }}
                  type="text"
                  inputProps={{ maxLength: 100 }}
                  inputRef={inputUserIdRef}
                  error={errorUserId}
                  helperText={errorUserIdMessage}
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="inputPassword" className="text-base">
                  {t("common.password")}
                </label>
                <TextField
                  id="inputPassword"
                  variant="outlined"
                  sx={{ margin: 0 }}
                  type="password"
                  inputProps={{ maxLength: 100 }}
                  inputRef={inputPasswordRef}
                  error={errorPassword}
                  helperText={errorPasswordMessage}
                />
              </div>
            </div>
            <div className="text-red-500 mt-5">{errorMessage}</div>
            <div className="mt-20">
              <Button className="w-full" variant="contained" onClick={loginClicked}>
                {t("adminLogin.login")}
              </Button>
            </div>
          </div>
        </div>
      </MainContent>
    </MainContainer>
  );
};
